<template>
  <div>
    <div class="md:hidden">
      <div class="flex justify-end mb-6">
        <transition name="fade">
          <div v-if="!hideSortMobile" class="sort-dropdown-wrapper">
            <div
              class="flex-grow-0 sort-dropdown"
              :class="{ open: mobileDropdownOpen }"
              v-click-outside="() => mobileDropdownOpen = false"
            >
              <div class="current" v-on:click="mobileDropdownOpen = !mobileDropdownOpen">
                {{ title }} sorted by {{ currentSortLabel }}
                <span v-if="sortAsc">&nbsp;↑</span>
                <span v-else>&nbsp;↓</span>
              </div>
              <div v-if="mobileDropdownOpen" class="choices">
                <div>
                  <div
                    v-for="(column, index) in sortableColumns"
                    :key="`choice-${index}`"
                    class="px-4 py-1/2 first:pt-2 last:pb-2 text-right cursor-pointer"
                    v-on:click="setSortByLabel(column.label)"
                  >
                    {{ column.label }}
                    <span class="cursor-pointer" :class="{ invisible: column.label !== currentSortLabel }" v-on:click="sortAsc = !sortAsc">
                      <span v-if="sortAsc">&nbsp;↑</span>
                      <span v-else>&nbsp;↓</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
      <div>
        <div
          class="cursor-pointer mb-8"
          v-for="(item, index) in sortedItems"
          :key="'item-' + index"
          v-on:click="$router.push(`/${item.uri}`)"
        >
          <div
            v-if="item.cover_image"
            class="w-full relative mb-1"
            :style="calcAspectRatioPadding(item.cover_image)"
          >
            <img
              class="absolute top-0 left-0 w-full h-full object-cover"
              v-lazy="item.cover_image.src"
              :data-srcset="item.cover_image.srcset"
            />
          </div>
          <div class="text-lg">{{ item.title }}</div>
          <div class="flex flex-row text-sm">
            <div class="flex-grow-1 whitespace-no-wrap">{{ item.date }}&nbsp;</div>
            <div v-if="item.location" class="text-gray-500 flex-grow-0 truncate">{{ item.location }}</div>
            <div v-else-if="item.type">{{ item.type }}</div>
          </div>
        </div>
      </div>
    </div>
    <div class="hidden md:block">
      <div class="flex justify-between border-b border-t border-black">
        <div class="select-none" :class="'w-1/' + columns.length" v-for="column in columns" :key="'heading-' + column.key">
          <div v-if="sortBy === 'date' && column.key === 'date'" class="px-4 py-2 flex flex-row">
            <div class="flex-grow-0 flex flex-row items-center cursor-pointer" v-on:click="sortAsc = !sortAsc">
              <div>{{ column.label }}</div>
              <div class="text-xs" v-if="sortAsc">&nbsp;↑</div>
              <div class="text-xs" v-else>&nbsp;↓</div>
            </div>
          </div>
          <div v-else-if="column.key === sortBy" class="px-4 py-2 flex flex-row">
            <div class="flex-grow-0 flex flex-row items-center cursor-pointer" v-on:click="sortAsc = !sortAsc">
              <div>{{ column.label }}</div>
              <div class="text-xs" v-if="sortAsc">&nbsp;↑</div>
              <div class="text-xs" v-else>&nbsp;↓</div>
            </div>
          </div>
          <div v-else class="px-4 py-2 flex flex-row">
            <div
              class="flex-grow-0 flex flex-row items-center cursor-pointer"
              v-on:click="sortBy = column.key"
            >
              <div>{{ column.label }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-col">
        <div
          class="flex justify-between border-b border-black cursor-pointer hover:underline"
          v-for="(item, index) in sortedItems"
          :key="'item-' + index"
          v-on:click="$router.push(`/${item.uri}`)"
        >
          <div :class="'w-1/' + columns.length" v-for="column in columns" :key="column.key">
            <div class="p-4">
              <div
                v-if="column.key === 'cover_image' && item[column.key]"
                class="w-full relative"
                :style="calcAspectRatioPadding(item[column.key])"
              >
                <img
                  v-if="column.key === 'cover_image'"
                  class="absolute top-0 left-0 w-full h-full object-cover"
                  v-lazy="item[column.key].src"
                  :data-srcset="item[column.key].srcset"
                />
              </div>
              <div v-else>{{ item[column.displayKey || column.key] }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vClickOutside from 'v-click-outside'

export default {
  name: 'IndexTable',

  props: ['title', 'items', 'columns', 'defaultSortBy', 'defaultSortAsc', 'hideSortMobile'],

  directives: {
    clickOutside: vClickOutside.directive
  },

  data () {
    return {
      sortBy: false,
      sortAsc: false,
      mobileDropdownOpen: false
    }
  },

  computed: {
    sortedItems () {
      if (!this.sortBy) {
        return this.items
      }

      const sk = this.sortBy
      const dir = this.sortAsc ? 1 : -1
      let sorted = [...this.items]
      sorted.sort((a, b) => {
        if (a[sk] > b[sk]) {
          return dir
        } else if (a[sk] < b[sk]) {
          return -dir
        } else {
          return 0
        }
      })

      return sorted
    },

    sortableColumns () {
      return this.columns.filter(c => c.key !== 'cover_image')
    },

    currentSortLabel () {
      const found = this.columns.find(e => e.key === this.sortBy)

      if (!found) {
        return null
      }

      return found.label
    }
  },

  mounted () {
    if (this.defaultSortBy) {
      this.sortBy = this.defaultSortBy
    }

    if (this.defaultSortAsc) {
      this.sortAsc = this.defaultSortAsc
    }
  },

  methods: {
    calcAspectRatioPadding (image) {
      if (!image || !image.dimensions) {
        return ''
      }

      return `padding-top:calc(100%/(${image.dimensions.width}/${image.dimensions.height}));`
    },

    setSortByLabel (label) {
      if (label === 'Date') {
        this.sortBy = 'date'
      } else {
        const found = this.columns.find(c => c.label === label)
        if (found) {
          this.sortBy = found.key
        }
      }
    }
  }
}
</script>

<style scoped lang="stylus">
.sort-dropdown-wrapper {
  position: fixed;
  display: flex;
  justify-content: flex-end;
  top: 0;
  left: 0;
  padding: 1rem;
  z-index: 30;
}

.sort-dropdown {
  font-size: 1.125rem;
}

.sort-dropdown .current {
  background: white;
  padding: 0.5rem 1rem;
  border-top: 1px solid black;
  border-left: 1px solid black;
  border-right: 1px solid black;
  border-bottom: 1px solid black;
  cursor: pointer;
}

.sort-dropdown.open .current {
  border-bottom: 1px solid white;
}

.sort-dropdown .choices {
  position: relative;
  top: 0;
  left: 0;
  width: 100%;
  height: 0;
  overflow: visible;
}

.sort-dropdown .choices > div {
  background: white;
  border-left: 1px solid black;
  border-right: 1px solid black;
  border-bottom: 1px solid black;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .25s;
}

.fade-enter-active {
  transition-delay: .25s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
